import { Background, Controls, Handle, MarkerType, Position, ReactFlow, ReactFlowProvider, type Edge, type Node, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useEffect, useMemo, useState } from 'react';
import type { CanvasAnalytics, CanvasDefinition, CanvasStepStats } from '../../../api/client.ts';
import { variantKey } from '../editor/commands.ts';
import { autoLayout } from '../editor/layout.ts';
import { portLabel, sum } from './labels.ts';

type Graph = Pick<CanvasDefinition, 'variants' | 'steps' | 'layout'>;
type Step = CanvasDefinition['steps'][string];
/** Stats nodes are taller than the editor's (numbers and ports), so rows of the editor layout get more room. */
const ROW_STRETCH = 1.6;

type StatsNodeData = { step: Step; typeLabel: string; stats: CanvasStepStats | undefined; ports: Array<{ port: string; label: string; count: number }>; finished: number };
type StatsNodeType = Node<StatsNodeData, 'stats'>;
type VariantNodeData = { name: string; percent: number; entries: number | null };
type VariantStatsNodeType = Node<VariantNodeData, 'variantStats'>;

const n = (v: number) => v.toLocaleString('en-US');

/** A step with its numbers: entered, waiting now, exited, failed (and sends for messages), each port with its count. */
function StatsNode({ data }: NodeProps<StatsNodeType>) {
  const { step, stats, ports } = data;
  const entered = stats?.entered ?? 0;
  const waiting = stats?.waiting ?? 0;
  const exited = sum(stats?.exited ?? {});
  const failed = stats?.failed ?? 0;
  const summary = [`entered ${n(entered)}`, `waiting ${n(waiting)}`, `exited ${n(exited)}`, `failed ${n(failed)}`, ...(step.type === 'message' ? [`sends ${n(stats?.sends ?? 0)}`] : []), ...ports.map((p) => `${p.label} ${n(p.count)}`), ...(data.finished ? [`finished ${n(data.finished)}`] : [])].join(', ');
  return (
    <div className="canvas-node step-overlay__node" role="group" aria-label={`Step ${step.name}: ${summary}`}>
      <Handle type="target" position={Position.Top} isConnectable={false} />
      <div className="canvas-node__head">
        <span className="canvas-node__type">{data.typeLabel}</span>
        {step.type === 'message' && <span className="canvas-node__badge">{n(stats?.sends ?? 0)} sends</span>}
      </div>
      <div className="canvas-node__name" title={step.name}>
        {step.name}
      </div>
      <div className="step-overlay__numbers">
        <span>
          <strong>{n(entered)}</strong> entered
        </span>
        <span className={waiting ? 'step-overlay__waiting' : undefined}>
          <strong>{n(waiting)}</strong> waiting
        </span>
        <span>
          <strong>{n(exited)}</strong> exited
        </span>
        <span className={failed ? 'field-error' : undefined}>
          <strong>{n(failed)}</strong> failed
        </span>
      </div>
      {(ports.length > 1 || data.finished > 0) && (
        <div className="canvas-node__ports">
          {ports.length > 1 &&
            ports.map((p) => (
              <span key={p.port} className="canvas-port">
                {p.label} {n(p.count)}
              </span>
            ))}
          {data.finished > 0 && <span className="canvas-port">finished {n(data.finished)}</span>}
        </div>
      )}
      {step.next.map((link, i) => (
        <Handle key={link.port} type="source" id={link.port} position={Position.Bottom} isConnectable={false} style={{ left: `${((i + 1) / (step.next.length + 1)) * 100}%` }} />
      ))}
    </div>
  );
}

function VariantStatsNode({ data }: NodeProps<VariantStatsNodeType>) {
  return (
    <div className="canvas-node canvas-node--variant" role="group" aria-label={`Variant ${data.name}: ${data.entries === null ? 'no entries' : `${n(data.entries)} entries`}`}>
      <div className="canvas-node__head">
        <span className="canvas-node__type">Variant · {data.percent}%</span>
      </div>
      <div className="canvas-node__name">{data.name}</div>
      <div className="step-overlay__numbers">
        <span>
          <strong>{n(data.entries ?? 0)}</strong> entries
        </span>
      </div>
      <Handle type="source" id="start" position={Position.Bottom} isConnectable={false} />
    </div>
  );
}

const nodeTypes = { stats: StatsNode, variantStats: VariantStatsNode };

export interface StepStatsOverlayProps {
  graph: Graph;
  analytics: CanvasAnalytics;
  typeLabel: (type: string) => string;
}

function Overlay({ graph, analytics, typeLabel }: StepStatsOverlayProps) {
  const [auto, setAuto] = useState<Record<string, { x: number; y: number }>>({});
  const keys = [...graph.variants.map((v) => variantKey(v.id)), ...Object.keys(graph.steps)];
  const missing = keys.filter((k) => !graph.layout?.[k]).join('|');
  useEffect(() => {
    if (!missing) return;
    let live = true;
    autoLayout({ schemaVersion: 1, variants: graph.variants, steps: graph.steps }).then(
      (positions) => live && setAuto(positions),
      // Without a layout the nodes still show, in a row.
      () => live && setAuto(Object.fromEntries(keys.map((k, i) => [k, { x: i * 260, y: 0 }]))),
    );
    return () => {
      live = false;
    };
    // Laid out again only when the set of unplaced nodes changes.
  }, [missing]);

  const { nodes, edges } = useMemo(() => {
    const stats = new Map(analytics.steps.map((s) => [s.stepId, s]));
    const entries = new Map(analytics.variants.map((v) => [v.variantId, v.entries]));
    const at = (key: string, i: number) => {
      const p = graph.layout?.[key] ?? auto[key] ?? { x: i * 260, y: 0 };
      return { x: p.x, y: Math.round(p.y * ROW_STRETCH) };
    };
    const nodes: Array<StatsNodeType | VariantStatsNodeType> = [
      ...graph.variants.map((v, i): VariantStatsNodeType => ({ id: variantKey(v.id), type: 'variantStats', position: at(variantKey(v.id), i), data: { name: v.name, percent: v.percent, entries: entries.get(v.id) ?? null } })),
      ...Object.values(graph.steps).map((s, i): StatsNodeType => {
        const st = stats.get(s.id);
        const ports = s.next.map((link) => ({ port: link.port, label: portLabel(graph, s.id, link.port), count: st?.advanced[link.port] ?? 0 }));
        const finished = s.next.filter((link) => link.stepId === null).reduce((acc, link) => acc + (st?.advanced[link.port] ?? 0), 0);
        return { id: s.id, type: 'stats', position: at(s.id, i), data: { step: s, typeLabel: typeLabel(s.type), stats: st, ports, finished } };
      }),
    ];
    const edges: Edge[] = [
      ...graph.variants
        .filter((v) => v.startStepId && graph.steps[v.startStepId])
        .map((v): Edge => ({ id: `${variantKey(v.id)}->${v.startStepId}`, source: variantKey(v.id), sourceHandle: 'start', target: v.startStepId!, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed }, label: n(entries.get(v.id) ?? 0) })),
      ...Object.values(graph.steps).flatMap((s) =>
        s.next
          .filter((link) => link.stepId && graph.steps[link.stepId])
          .map((link): Edge => {
            const count = stats.get(s.id)?.advanced[link.port] ?? 0;
            return { id: `${s.id}:${link.port}->${link.stepId}`, source: s.id, sourceHandle: link.port, target: link.stepId!, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed }, ...(s.next.length > 1 ? {} : { label: n(count) }), className: count ? 'step-overlay__edge--used' : undefined };
          }),
      ),
    ];
    return { nodes, edges };
  }, [graph, analytics, auto, typeLabel]);

  // React Flow fits the view once, on its first render: wait for the automatic layout of unplaced nodes.
  const ready = keys.every((k) => graph.layout?.[k] ?? auto[k]);
  if (!ready) return <p aria-busy="true">Laying out the graph…</p>;
  return (
    <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} nodesDraggable={false} nodesConnectable={false} elementsSelectable={false} edgesFocusable={false} minZoom={0.1} maxZoom={1.5} fitView fitViewOptions={{ minZoom: 0.5, maxZoom: 1 }} proOptions={{ hideAttribution: true }}>
      <Background />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
}

/**
 * The Canvas graph with the period's numbers on it (BB-123): each step shows entered / waiting now / exited / failed
 * (sends for messages) and its ports with how many advanced; a single-port edge carries its count (a branching step
 * shows the counts on its ports, since edge labels can hide under the nodes they pass). Read-only React Flow
 * with the editor's layout (`layout.ts`, stored positions first) and node styles, but its own nodes: the editor's
 * nodes are built around editing actions and validation.
 */
export function StepStatsOverlay(props: StepStatsOverlayProps) {
  return (
    <div className="step-overlay" aria-label="Canvas graph with step numbers" role="region">
      <ReactFlowProvider>
        <Overlay {...props} />
      </ReactFlowProvider>
    </div>
  );
}
